import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  History, 
  MapPin, 
  Bell, 
  Settings, 
  LogOut,
  Menu,
  X,
  Package,
  Receipt,
  ArrowLeft
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerLayout = ({ children }) => {
  const { user, logout, embedded, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isImpersonating = !!localStorage.getItem('admin_token');

  const handleBackToAdmin = async () => {
    await stopImpersonation();
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/notifications`);
        setUnreadCount(response.data.unread_count || 0);
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };
    fetchNotifications();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/create-waybill', icon: FileText, label: 'Create Waybill' },
    { to: '/history', icon: History, label: 'History' },
    { to: '/billing', icon: Receipt, label: 'Billing' },
    { to: '/tracking', icon: MapPin, label: 'Tracking' },
    { to: '/notifications', icon: Bell, label: 'Notifications', badge: unreadCount },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-layout">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-indigo-600 text-white px-4 py-2 flex items-center justify-between z-[60] sticky top-0" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">Viewing as:</span>
            <span>{user?.company_name || 'Customer'}</span>
          </div>
          <button
            onClick={handleBackToAdmin}
            className="flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-md transition-colors"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Admin
          </button>
        </div>
      )}

      {/* Top Navigation — hidden in embedded/iframe mode */}
      {!embedded && (
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50" data-testid="customer-navbar">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <NavLink to="/dashboard" className="flex items-center" data-testid="logo-link">
                <img src="/cargodash-logo.svg" alt="CargoDash" className="h-9" />
              </NavLink>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors relative ${
                      isActive
                        ? 'text-teal-700 bg-teal-50'
                        : 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 mr-1.5" />
                  {item.label}
                  {item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
                  <p className="text-xs text-slate-500">{user?.company_name}</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="h-10 w-10 rounded-full" />
                  ) : (
                    <span className="text-teal-700 font-semibold text-sm">
                      {user?.full_name?.charAt(0) || 'U'}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="hidden sm:flex items-center px-3 py-2 text-sm text-slate-600 hover:text-red-600 transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                      isActive
                        ? 'text-teal-700 bg-teal-50'
                        : 'text-slate-600 hover:text-teal-700 hover:bg-slate-50'
                    }`
                  }
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                  {item.badge > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm text-red-600 font-medium rounded-lg hover:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>
      )}

      {/* Main Content */}
      <main className="px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default CustomerLayout;
